import React, { useCallback, useEffect, useMemo, useState } from "react";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Alert, Chip, CircularProgress } from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { AppButton } from "../../components/ui";
import AccountsAPI from "../../services/AccountsAPI";
import {
  formatSubscriptionDate,
  formatSubscriptionLabel,
  getRoleDisplayName,
  getSubscriptionState,
} from "../../utility/subscriptionAccess";
import "./UpgradePage.scss";

const LICENSE_FEATURES = [
  {
    icon: <SchoolRoundedIcon />,
    title: "Annual classroom license",
    description: "Full web access for one year at $200 per classroom.",
  },
  {
    icon: <GroupsRoundedIcon />,
    title: "Join codes for students",
    description: "Share experiences across your classes from one educator account.",
  },
  {
    icon: <InsightsRoundedIcon />,
    title: "Educator analytics",
    description: "Keep dashboard access and AI-assisted student insights active for the full term.",
  },
  {
    icon: <RocketLaunchRoundedIcon />,
    title: "All current content",
    description: "Unlock the full 10k Science web viewer library and future releases during the license term.",
  },
];

const DEFAULT_UPGRADE_DETAILS = [
  {
    tone: "success",
    text: "Your subscription changes from trial/free to subscribed.",
  },
  {
    tone: "success",
    text: "Your role changes from consumer to educator automatically.",
  },
  {
    tone: "success",
    text: "Your educator access stays active for one year from checkout.",
  },
  {
    tone: "success",
    text: "The classroom license renews automatically every year until you turn off auto-renew.",
  },
  {
    tone: "warning",
    text: "Free accounts can still sign in, but viewer access and join-code tools are locked until the license is active.",
  },
];

const renderDetailIcon = (tone) =>
  tone === "warning" ? (
    <WarningAmberRoundedIcon color="warning" />
  ) : (
    <CheckCircleRoundedIcon color="success" />
  );

const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;
const wait = (milliseconds) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });

const UpgradePage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checkoutError, setCheckoutError] = useState("");
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
  const [activationState, setActivationState] = useState("idle");
  const { refreshAuthSession, userId } = auth;
  const syncUpgradedAccount = useCallback(
    async ({ showDelayedState = false } = {}) => {
      if (!userId) {
        return false;
      }

      setActivationState("pending");

      for (let attempt = 0; attempt < 12; attempt += 1) {
        try {
          const response = await AccountsAPI.get(`/users/${userId}`, {
            requiresAuth: true,
          });
          const user = getApiResponseData(response);
          const subscription = getSubscriptionState({
            status: user?.subscription?.type,
            source: user?.subscription?.source,
            expirationDate: user?.subscription?.expirationDate,
            renews: user?.subscription?.renews,
            cancelAtPeriodEnd: user?.subscription?.cancelAtPeriodEnd,
          });

          if (subscription.isSubscribed) {
            await refreshAuthSession();
            setActivationState("complete");
            return true;
          }
        } catch (error) {
          console.error("Unable to refresh upgraded account", error);
        }

        await wait(2500);
      }

      if (showDelayedState) {
        setActivationState("delayed");
      } else {
        setActivationState("idle");
      }

      return false;
    },
    [refreshAuthSession, userId]
  );

  const currentSubscription = useMemo(
    () =>
      getSubscriptionState({
        status: auth.subscriptionStatus,
        source: auth.subscriptionSource,
        expirationDate: auth.subscriptionEndsAt,
        renews: auth.subscriptionRenews,
        cancelAtPeriodEnd: auth.subscriptionCancelAtPeriodEnd,
      }),
    [
      auth.subscriptionRenews,
      auth.subscriptionCancelAtPeriodEnd,
      auth.subscriptionEndsAt,
      auth.subscriptionSource,
      auth.subscriptionStatus,
    ]
  );
  const checkoutStatus = searchParams.get("checkout");

  useEffect(() => {
    if (checkoutStatus !== "success" || !userId) {
      return undefined;
    }

    let cancelled = false;

    syncUpgradedAccount({ showDelayedState: true }).then(() => {
      if (cancelled) {
        return;
      }
    });

    return () => {
      cancelled = true;
    };
  }, [checkoutStatus, syncUpgradedAccount, userId]);

  const handleUpgrade = useCallback(async () => {
    setCheckoutError("");
    setIsRedirectingToStripe(true);

    try {
      const response = await AccountsAPI.post(
        "/users/upgrade",
        {},
        { requiresAuth: true }
      );
      const payload = getApiResponseData(response);

      if (!payload?.url) {
        throw new Error("Checkout URL missing from upgrade response");
      }

      window.location.assign(payload.url);
    } catch (error) {
      console.error("Unable to start Stripe checkout", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Unable to start checkout right now. Please try again.";

      setCheckoutError(errorMessage);
      setIsRedirectingToStripe(false);

      if (
        error?.response?.status === 409 &&
        errorMessage.includes("active classroom subscription already exists")
      ) {
        void syncUpgradedAccount();
      }
    }
  }, [syncUpgradedAccount]);

  const statusLabel = formatSubscriptionLabel(
    currentSubscription.status,
    currentSubscription.hasClassroomAccess
  );
  const formattedAccessDate = auth.subscriptionEndsAt
    ? formatSubscriptionDate(auth.subscriptionEndsAt)
    : null;
  const pageCopy = useMemo(() => {
    if (currentSubscription.isSubscribed) {
      const hasStripeRenewal = currentSubscription.source === "stripe" && currentSubscription.renews;
      const hasStripeCancellation =
        currentSubscription.source === "stripe" && !currentSubscription.renews;

      let title = "Your classroom license is active";
      let subtitle =
        "Your educator access is already unlocked. Use this page to review what is included with your classroom license.";

      if (hasStripeRenewal && formattedAccessDate) {
        subtitle = `Your educator access is already unlocked. This classroom license renews on ${formattedAccessDate}, and you can manage billing from your account page.`;
      } else if (hasStripeCancellation && formattedAccessDate) {
        title = `Your classroom license stays active through ${formattedAccessDate}`;
        subtitle = `Your educator access is already unlocked. Auto-renew is off, so this license stays active through ${formattedAccessDate}. You can turn auto-renew back on from your account page at any time.`;
      } else if (formattedAccessDate) {
        subtitle = `Your educator access is already unlocked through ${formattedAccessDate}. Use this page to review what is included with your classroom license.`;
      }

      return {
        title,
        subtitle,
        planKicker: "Your active plan",
        planTitle:
          currentSubscription.source === "stripe" ? "$200 / year" : "Active access",
        planBadge:
          hasStripeRenewal
            ? "Auto-renew on"
            : currentSubscription.source === "stripe"
            ? "Educator access"
            : "Plan active",
        detailsTitle: "What your license includes",
        details: [
          {
            tone: "success",
            text: "Full web viewer access and join-code tools are already unlocked for your account.",
          },
          {
            tone: "success",
            text: "You can share experiences across your classes from one educator account.",
          },
          {
            tone: "success",
            text: formattedAccessDate
              ? `Your educator access stays active through ${formattedAccessDate}.`
              : "Your educator access remains active on your current term.",
          },
          {
            tone: hasStripeCancellation ? "warning" : "success",
            text: hasStripeRenewal
              ? "Auto-renew is on, and you can turn it off anytime from your account page."
              : hasStripeCancellation
              ? "Auto-renew is currently off. You can turn it back on anytime from your account page."
              : "Plan details and account settings are available from your account page.",
          },
        ],
        primaryAction: {
          component: Link,
          to: "/account",
          label: currentSubscription.source === "stripe" ? "Manage Billing" : "Open Account",
        },
        secondaryAction: {
          component: Link,
          to: "/dashboard",
          label: "Return to Dashboard",
        },
      };
    }

    return {
      title: "Upgrade to a classroom license",
      subtitle:
        "Trial accounts keep full viewer and join-code access for 7 days. Paid classroom licenses renew annually until you turn off auto-renew from your account page. Free accounts keep dashboard access, but viewer and classroom tools stay locked until the classroom license is active.",
      planKicker: "Classroom license",
      planTitle: "$200 / year",
      planBadge: "Educator access",
      detailsTitle: "What changes after upgrade",
      details: DEFAULT_UPGRADE_DETAILS,
      primaryAction: {
        label: currentSubscription.isTrial ? "Upgrade before trial ends" : "Upgrade now",
        onClick: handleUpgrade,
        loading: isRedirectingToStripe,
        loadingLabel: "Redirecting to Stripe...",
        disabled: false,
      },
      secondaryAction: {
        onClick: () => navigate("/account"),
        label: "Back to Account",
      },
    };
  }, [
    currentSubscription.isSubscribed,
    currentSubscription.isTrial,
    currentSubscription.renews,
    currentSubscription.source,
    formattedAccessDate,
    handleUpgrade,
    isRedirectingToStripe,
    navigate,
  ]);

  return (
    <main className="upgradePage">
      <section className="upgradePage__hero">
        <div className="upgradePage__eyebrow">10k Science Web</div>
        <div className="upgradePage__headlineRow">
          <div>
            <h1 className="upgradePage__title">{pageCopy.title}</h1>
            <p className="upgradePage__subtitle">{pageCopy.subtitle}</p>
          </div>
          <div className="upgradePage__statusCard">
            <div className="upgradePage__statusLabel">Current account</div>
            <Chip
              className="upgradePage__statusChip"
              color={currentSubscription.hasClassroomAccess ? "success" : "info"}
              label={statusLabel}
            />
            <div className="upgradePage__statusMeta">
              Role: {getRoleDisplayName({
                roleName: auth.roleName,
                roleId: auth.roleId,
                isRoleNameLoading: auth.isAccountProfileLoading,
              })}
            </div>
            {auth.subscriptionEndsAt && (
              <div className="upgradePage__statusMeta">
                {currentSubscription.source === "stripe" && currentSubscription.renews
                  ? `Renews on ${formatSubscriptionDate(auth.subscriptionEndsAt)}`
                  : `Access through ${formatSubscriptionDate(auth.subscriptionEndsAt)}`}
              </div>
            )}
            {currentSubscription.source === "stripe" && !currentSubscription.renews && (
              <div className="upgradePage__statusMeta">
                Auto-renew is off for this license term.
              </div>
            )}
          </div>
        </div>

        {checkoutStatus === "cancelled" && (
          <Alert severity="info" className="upgradePage__alert">
            Checkout was cancelled. Your account is unchanged.
          </Alert>
        )}
        {checkoutStatus === "success" && activationState === "pending" && (
          <Alert severity="info" className="upgradePage__alert">
            Payment received. We&apos;re activating your educator access now.
          </Alert>
        )}
        {checkoutStatus === "success" && activationState === "complete" && (
          <Alert severity="success" className="upgradePage__alert">
            Your classroom license is active and your account has been upgraded to educator access.
          </Alert>
        )}
        {checkoutStatus === "success" && activationState === "delayed" && (
          <Alert severity="warning" className="upgradePage__alert">
            Payment succeeded, but the dashboard has not refreshed yet. Wait a few seconds, then
            refresh this page or sign out and back in.
          </Alert>
        )}
        {checkoutError && (
          <Alert severity="error" className="upgradePage__alert">
            {checkoutError}
          </Alert>
        )}
      </section>

      <section
        className={`upgradePage__body${currentSubscription.isSubscribed ? " upgradePage__body--singleColumn" : ""}`}
      >
        {!currentSubscription.isSubscribed && (
          <div className="upgradePage__planCard">
            <div className="upgradePage__planHeader">
              <div>
                <div className="upgradePage__planKicker">{pageCopy.planKicker}</div>
                <h2 className="upgradePage__planTitle">{pageCopy.planTitle}</h2>
              </div>
              <div className="upgradePage__planBadge">{pageCopy.planBadge}</div>
            </div>

            <ul className="upgradePage__featureList">
              {LICENSE_FEATURES.map((feature) => (
                <li key={feature.title} className="upgradePage__featureItem">
                  <span className="upgradePage__featureIcon" aria-hidden="true">
                    {feature.icon}
                  </span>
                  <div className="upgradePage__featureBody">
                    <h3 className="upgradePage__featureTitle">{feature.title}</h3>
                    <p className="upgradePage__featureDescription">{feature.description}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="upgradePage__ctaRow">
              <AppButton
                kind="primary"
                loading={pageCopy.primaryAction.loading}
                loadingLabel={pageCopy.primaryAction.loadingLabel}
                disabled={pageCopy.primaryAction.disabled}
                {...(pageCopy.primaryAction.component
                  ? {
                      component: pageCopy.primaryAction.component,
                      to: pageCopy.primaryAction.to,
                    }
                  : {})}
                {...(pageCopy.primaryAction.onClick
                  ? { onClick: pageCopy.primaryAction.onClick }
                  : {})}
              >
                {pageCopy.primaryAction.label}
              </AppButton>
              <AppButton
                kind="secondary"
                {...(pageCopy.secondaryAction.component
                  ? {
                      component: pageCopy.secondaryAction.component,
                      to: pageCopy.secondaryAction.to,
                    }
                  : {})}
                {...(pageCopy.secondaryAction.onClick
                  ? { onClick: pageCopy.secondaryAction.onClick }
                  : {})}
              >
                {pageCopy.secondaryAction.label}
              </AppButton>
            </div>
          </div>
        )}

        <div className="upgradePage__detailsCard">
          <h2 className="upgradePage__detailsTitle">{pageCopy.detailsTitle}</h2>
          <ul className="upgradePage__detailList">
            {pageCopy.details.map((detail) => (
              <li key={detail.text} className="upgradePage__detailItem">
                <span className="upgradePage__bulletIcon" aria-hidden="true">
                  {renderDetailIcon(detail.tone)}
                </span>
                <p className="upgradePage__detailText">{detail.text}</p>
              </li>
            ))}
          </ul>

          {currentSubscription.isSubscribed && (
            <div className="upgradePage__inlineActions">
              <AppButton
                kind="primary"
                {...(pageCopy.primaryAction.component
                  ? {
                      component: pageCopy.primaryAction.component,
                      to: pageCopy.primaryAction.to,
                    }
                  : {})}
                {...(pageCopy.primaryAction.onClick
                  ? { onClick: pageCopy.primaryAction.onClick }
                  : {})}
              >
                {pageCopy.primaryAction.label}
              </AppButton>
              <AppButton
                kind="secondary"
                {...(pageCopy.secondaryAction.component
                  ? {
                      component: pageCopy.secondaryAction.component,
                      to: pageCopy.secondaryAction.to,
                    }
                  : {})}
                {...(pageCopy.secondaryAction.onClick
                  ? { onClick: pageCopy.secondaryAction.onClick }
                  : {})}
              >
                {pageCopy.secondaryAction.label}
              </AppButton>
            </div>
          )}

          {activationState === "pending" && (
            <div className="upgradePage__loadingState">
              <CircularProgress size={20} />
              <span>Refreshing your account permissions...</span>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default UpgradePage;
